import ReactECharts from 'echarts-for-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { Link, useNavigate } from 'react-router-dom';

import type { ProjectListRow } from '../../entities/projects.stat';
import { getProviderCapabilities } from '../../entities/provider';
import { getProjectsStats } from '../../shared/api/dashboard';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjects } from '../../shared/contexts/Projects.provider';
import { useConnectedProviders } from '../../shared/hooks/use-connected-providers';
import { asISODate, clamp, fmtDaysFromHours, fmtPct, psiBadge, safeNum } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, OtherProjectsCard, StatEmpty, StatError, StatLoading } from '../../widgets/stats';
import { ProviderBadge } from '../../widgets/ui/ProviderBadge';

type SortKey =
  | 'projectName'
  | 'psi'
  | 'completedCount'
  | 'throughputAccuracyPercent'
  | 'reopenRatePercent'
  | 'avgCycleTimeHours';
type SortDir = 'asc' | 'desc';

export const ProjectsStatsPage: React.FC = () => {
  const navigate = useNavigate();
  const dateRangeCtx = useDateRange();
  const projectsCtx = useProjects();
  const setDashHeader = useSetDashboardHeader();
  const { isConnected, isLoading: connLoading } = useConnectedProviders();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<ProjectListRow[]>([]);

  const [query, setQuery] = useState('');
  const [psiFilter, setPsiFilter] = useState<'all' | 'stable' | 'watch' | 'risk'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('psi');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    const run = async () => {
      if (connLoading) return;
      if (!isConnected.jira) {
        setLoading(false);
        return;
      }
      if (!dateRangeCtx?.from || !dateRangeCtx?.to) return;

      try {
        setLoading(true);
        setError(null);

        const range = { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) };
        // This endpoint is Jira-only — Trello/Standalone projects get their own cards below.
        const projectIds = (projectsCtx ?? [])
          .filter((p: any) => (p.provider ?? 'jira') === 'jira')
          .map((p: any) => p._id)
          .filter(Boolean);

        const { stat } = await getProjectsStats({ projectIds, range });
        setRows(stat ?? []);
      } catch (e) {
        console.error(e);
        setError('Failed to load projects stats.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [dateRangeCtx?.from, dateRangeCtx?.to, projectsCtx, connLoading, isConnected.jira]);

  const normalizedRows = useMemo(() => {
    const nameById = new Map<string, string>();
    (projectsCtx ?? []).forEach((p: any) => nameById.set(p._id, p.name));

    return (rows ?? []).map((r) => ({
      ...r,
      projectName: r.projectName || nameById.get(r.projectId) || r.projectId,
    }));
  }, [rows, projectsCtx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return normalizedRows.filter((r) => {
      const matchesQuery =
        !q || (r.projectName ?? '').toLowerCase().includes(q) || (r.projectId ?? '').toLowerCase().includes(q);

      const psi = safeNum(r.psi);
      const matchesPsi =
        psiFilter === 'all'
          ? true
          : psiFilter === 'stable'
            ? psi >= 80
            : psiFilter === 'watch'
              ? psi >= 60 && psi < 80
              : psi < 60;

      return matchesQuery && matchesPsi;
    });
  }, [normalizedRows, query, psiFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;

    const getValue = (r: ProjectListRow): string | number => {
      if (sortKey === 'projectName') return r.projectName ?? '';
      return safeNum((r as any)[sortKey], 0);
    };

    return [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const avgPsi = total ? filtered.reduce((s, r) => s + safeNum(r.psi), 0) / total : 0;

    const completed = filtered.reduce((s, r) => s + safeNum(r.completedCount), 0);
    const created = filtered.reduce((s, r) => s + safeNum(r.createdCount), 0);

    const throughput = created > 0 ? (completed / created) * 100 : 0;

    const riskCount = filtered.filter((r) => safeNum(r.psi) < 60).length;
    const stableCount = filtered.filter((r) => safeNum(r.psi) >= 80).length;

    return { total, avgPsi, throughput, riskCount, stableCount };
  }, [filtered]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'projectName' ? 'asc' : 'desc');
    }
  };

  const psiChartOption = useMemo(() => {
    const top = [...filtered].sort((a, b) => safeNum(a.psi) - safeNum(b.psi)).slice(0, 12);
    const labels = top.map((x) => x.projectName);
    const values = top.map((x) => clamp(safeNum(x.psi), 0, 100));

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 20, right: 16, top: 10, bottom: 70, containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { rotate: 18, interval: 0 } },
      yAxis: { type: 'value', min: 0, max: 100 },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: 18,
          label: { show: true, position: 'top', formatter: (p: any) => `${Math.round(p.value)}` },
          itemStyle: { borderRadius: [8, 8, 0, 0] },
        },
      ],
    };
  }, [filtered]);

  const otherProjects = useMemo(
    () =>
      (projectsCtx ?? []).filter((p: any) => p.provider && !getProviderCapabilities(p.provider).supportsStudioOverview),
    [projectsCtx],
  );

  const goProject = (id: string) => navigate(`/dashboard/projects/${id}`);
  const rangeLabel = useMemo(() => {
    if (!dateRangeCtx?.from || !dateRangeCtx?.to) return '';
    return `${dateRangeCtx.from.toLocaleDateString()} → ${dateRangeCtx.to.toLocaleDateString()}`;
  }, [dateRangeCtx?.from, dateRangeCtx?.to]);

  useEffect(() => {
    setDashHeader({
      component: 'projects',
      breadcrumb: (
        <Link to="/dashboard" className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message={'Studio'} position={'bottom'}>
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: (
        <span className="inline-flex items-center gap-2">
          Projects
          {isConnected.jira && <ProviderBadge provider="jira" />}
        </span>
      ),
      subtitle: rangeLabel ? (
        <span>
          Range: <span className="font-medium">{rangeLabel}</span>
        </span>
      ) : undefined,
    });
    return () => setDashHeader({});
  }, [rangeLabel, setDashHeader, isConnected.jira]);

  if (connLoading || loading) return <StatLoading />;
  if (isConnected.jira && error) return <StatError message={error} />;

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        {otherProjects.length > 0 && <OtherProjectsCard projects={otherProjects} />}

        {!isConnected.jira && otherProjects.length === 0 && (
          <StatEmpty message="Connect Jira, Trello, or start a standalone project to see your dashboard." />
        )}

        {isConnected.jira && (
          <>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <input
                className="input input-bordered w-full md:w-72 bg-base-100"
                placeholder="Search project…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <select
                className="select select-bordered w-full md:w-48 bg-base-100"
                value={psiFilter}
                onChange={(e) => setPsiFilter(e.target.value as any)}
              >
                <option value="all">All PSI</option>
                <option value="stable">Stable (80–100)</option>
                <option value="watch">Watch (60–79)</option>
                <option value="risk">Risk (&lt; 60)</option>
              </select>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <KpiCard
                label={
                  <>
                    Projects <InfoTip text={'Filtered projects count.\nThis page is your "portfolio view".'} />
                  </>
                }
                value={summary.total}
                footer={`Stable: ${summary.stableCount} · Risk: ${summary.riskCount}`}
              />

              <KpiCard
                label={
                  <>
                    Avg PSI <InfoTip text={'Average PSI across filtered projects.\nHigher = more stable portfolio.'} />
                  </>
                }
                value={Math.round(summary.avgPsi)}
                footer="Portfolio stability"
              />

              <KpiCard
                label={
                  <>
                    Throughput (portfolio)
                    <InfoTip text={'Throughput accuracy = completed / created\nAggregated across filtered projects.'} />
                  </>
                }
                value={fmtPct(summary.throughput)}
                footer='Best for "no due dates" reality'
              />

              <KpiCard
                label={
                  <>
                    What to click
                    <InfoTip text={'Sort by PSI (asc) to see the riskiest projects.\nClick any row to drill down.'} />
                  </>
                }
              >
                <div className="text-sm mt-1 opacity-80">
                  Drill-down: <span className="font-medium">Projects → Project → Users</span>
                </div>
                <button
                  className="btn btn-sm btn-outline mt-3 w-fit"
                  onClick={() => {
                    setQuery('');
                    setPsiFilter('all');
                    setSortKey('psi');
                    setSortDir('asc');
                  }}
                >
                  Show risk first
                </button>
              </KpiCard>
            </div>

            {/* Chart */}
            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-4">
                <div className="text-lg font-semibold flex items-center">
                  Top risk projects by PSI
                  <InfoTip text={'Lowest PSI first.\nThis chart helps you spot where to look immediately.'} />
                </div>
                <div className="text-sm opacity-70">Click bars or rows to drill down</div>

                <div className="h-[360px] mt-2">
                  <ReactECharts
                    option={psiChartOption}
                    style={{ height: '100%' }}
                    onEvents={{
                      click: (p: any) => {
                        const label = p?.name;
                        const row = filtered.find((r) => r.projectName === label);
                        if (row?.projectId) goProject(row.projectId);
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-0">
                <div className="p-4 flex items-center justify-between">
                  <div className="text-lg font-semibold">All projects</div>
                  <div className="text-xs opacity-60">Click a row to open project dashboard</div>
                </div>

                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th className="cursor-pointer" onClick={() => toggleSort('projectName')}>
                          Project
                        </th>
                        <th className="cursor-pointer text-right" onClick={() => toggleSort('psi')}>
                          PSI
                        </th>
                        <th className="cursor-pointer text-right" onClick={() => toggleSort('completedCount')}>
                          Done
                        </th>
                        <th
                          className="cursor-pointer text-right"
                          onClick={() => toggleSort('throughputAccuracyPercent')}
                        >
                          Throughput
                        </th>
                        <th className="cursor-pointer text-right" onClick={() => toggleSort('reopenRatePercent')}>
                          Reopen
                        </th>
                        <th className="cursor-pointer text-right" onClick={() => toggleSort('avgCycleTimeHours')}>
                          Cycle
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sorted.map((r) => {
                        const psi = safeNum(r.psi);
                        const b = psiBadge(psi);

                        return (
                          <tr key={r.projectId} className="hover cursor-pointer" onClick={() => goProject(r.projectId)}>
                            <td className="py-3">
                              <div className="flex flex-col">
                                <span className="font-medium">{r.projectName}</span>
                                <span className="text-xs opacity-60">{r.projectId}</span>
                              </div>
                            </td>

                            <td className="text-right">
                              <span className={`badge ${b.cls}`}>{Math.round(psi)}</span>
                            </td>

                            <td className="text-right">{safeNum(r.completedCount)}</td>
                            <td className="text-right">{fmtPct(r.throughputAccuracyPercent)}</td>
                            <td className="text-right">{fmtPct(r.reopenRatePercent)}</td>
                            <td className="text-right">{fmtDaysFromHours(r.avgCycleTimeHours)}</td>
                          </tr>
                        );
                      })}

                      {!sorted.length && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center opacity-60">
                            No projects match current filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="p-4 text-xs opacity-60">
                  Sorting tip: PSI (asc) → find fires. Throughput (desc) → find best-flow teams.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
